import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { AppButton } from "site/ui/base/Button";
import { useNavigate } from "react-router-dom";
import { routeNames } from "utils";

const HeroContainer = styled.section`
  position: relative;
  width: 100%;
  height: 100vh;
  min-height: 600px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 100vh;
    width: 100%;
  }
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("/assets/hero-bg.jpg");
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  z-index: 0;
  margin: 0;
  padding: 0;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7));
    z-index: 1;
  }
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  width: 100%;
  padding: 0 20px;
  text-align: center;
  z-index: 2;
  position: relative;
`;

const HeroTitle = styled(motion.h1)`
  font-family: "Manrope", sans-serif;
  font-size: 3rem;
  font-weight: 600;
  color: white;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-family: "Manrope", sans-serif;
  font-size: 1.1rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  max-width: 800px;
  margin: 0 auto 2.5rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    max-width: 90%;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 1rem;

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
    max-width: 280px;
    margin: 0 auto;
  }
`;

export const Hero: React.FC = () => {
  const navigate = useNavigate();

  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  const subtitleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, delay: 0.3, ease: "easeOut" },
    },
  };

  const buttonGroupVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: 0.6,
        ease: "easeOut",
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <HeroContainer>
      <HeroBackground />

      <ContentWrapper>
        <HeroTitle initial="hidden" animate="visible" variants={titleVariants}>
          Turn Applications into Appointments
        </HeroTitle>

        <HeroSubtitle
          initial="hidden"
          animate="visible"
          variants={subtitleVariants}
        >
          Structured courses and interview preparation tools for competitive healthcare recruitment
        </HeroSubtitle>

        <ButtonGroup
          initial="hidden"
          animate="visible"
          variants={buttonGroupVariants}
        >
          <AppButton variant="primary" onClick={() => window.location.href = routeNames.signUp}>
            Explore resources for free
          </AppButton>

          <AppButton variant="secondary" onClick={() => navigate(routeNames.guide)}>
            Learn more
          </AppButton>
        </ButtonGroup>
      </ContentWrapper>
    </HeroContainer>
  );
};
